import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const StoneRequestDialog = ({ isOpen, onClose, defaultStone = '' }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    stone_name: defaultStone,
    size_carat: '',
    budget_range: '',
    other_details: '',
    phone: user?.phone || ''
  });

  useEffect(() => {
    if (user?.phone) {
      setFormData(prev => ({ ...prev, phone: user.phone }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/stone-requests', formData);
      toast.success('Stone request submitted! We will contact you shortly.');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-serif">Request a Stone</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label htmlFor="stone_name">Stone Name *</Label>
            <Input
              id="stone_name"
              name="stone_name"
              value={formData.stone_name}
              onChange={handleChange}
              placeholder="e.g., Emerald, Ruby, Sapphire"
              required
            />
          </div>

          <div>
            <Label htmlFor="size_carat">Size / Carat Weight *</Label>
            <Input
              id="size_carat"
              name="size_carat"
              value={formData.size_carat}
              onChange={handleChange}
              placeholder="e.g., 2-3 carats, 10mm round"
              required
            />
          </div>

          <div>
            <Label htmlFor="budget_range">Budget Range *</Label>
            <Input
              id="budget_range"
              name="budget_range"
              value={formData.budget_range}
              onChange={handleChange}
              placeholder="e.g., $5,000 - $10,000"
              required
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Your phone number"
              required
            />
          </div>

          <div>
            <Label htmlFor="other_details">Additional Details</Label>
            <Textarea
              id="other_details"
              name="other_details"
              value={formData.other_details}
              onChange={handleChange}
              placeholder="Any specific requirements, color preferences, cut preferences, intended use, etc."
              rows={4}
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Email:</strong> {user?.email}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              We'll contact you at this email and phone number with available options.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-black hover:bg-gray-800"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoneRequestDialog;
